import './form.scss'
import { tmrResourceFormController } from 'tmr/controllers/resourceForm/resourceForm'
import templateUrl from './form.html'

/**
 * Administer RFID Readers route
 *
 * @namespace routes.AdminRfidReaders
 * @type {TmrRoute}
 */
export const routes = [
  {
    name: 'devices.rfidReaders.add',
    resolve: {
      antennas: () => [],
    },
  },
  {
    name: 'devices.rfidReaders.view.edit',
    resolve: {
      antennas: /*@ngInject*/ (Antenna, resource) =>
        Antenna.query({ 'rfidReader.id': resource.id }).$promise,
    },
  },
]

/**
 * RFID Reader add/edit form component.
 *
 * @type {ngComponent}
 */
export const tmrAdminRfidReaderForm = {
  ngType: 'component',
  templateUrl,
  controller: 'tmrAdminRfidReaderFormController',
  bindings: {
    resource: '<',
    toast: '<',
    antennas: '<',
  },
}

/**
 * Controller of component {@link tmrAdminRfidReaderForm}.
 *
 * @type {ngController}
 */
export class tmrAdminRfidReaderFormController extends tmrResourceFormController {
  constructor(
    $scope,
    $state,
    $stateParams,
    TmrInvalidFormError,
    TmrToast,
    tmrRfidReaderTypes,
    tmrRfidReaderEmulatorModes,
    tmrRfidReaderImpinjReaderModes,
    tmrRfidReaderImpinjSearchModes,
    tmrRfidReaderStartStopTypes,
    tmrRfidReaderConnectionModes,
    TmrSerialQ,
    Antenna,
    Place,
    Zone
  ) {
    'ngInject'
    super($scope, $state, $stateParams, TmrInvalidFormError, TmrToast)
    Object.assign(this, {
      tmrRfidReaderTypes,
      tmrRfidReaderEmulatorModes,
      tmrRfidReaderImpinjReaderModes,
      tmrRfidReaderImpinjSearchModes,
      tmrRfidReaderStartStopTypes,
      tmrRfidReaderConnectionModes,
      TmrSerialQ,
      Antenna,
      Place,
      Zone,
    })
  }

  $onInit() {
    super.$onInit()
    this.antennaCards = []
    this.antennasToDelete = []
    this.tmpAntennas = {}
    this.placeZones = []
    this.updateAntennaCards()
    this.updatePlaceZones()
    this.gpoModes = {
      ImpinjSpeedwayOctaneDto: [
        'NORMAL',
        'PULSED',
        'READER_OPERATIONAL_STATUS',
        'LLRP_CONNECTION_STATUS',
        'READER_INVENTORY_STATUS',
        'NETWORK_CONNECTION_STATUS',
        'READER_INVENTORY_TAGS_STATUS',
      ],
      ZebraReaderDto: ['NORMAL', 'PULSED'],
      CaenQuattroDto: ['NORMAL', 'PULSED'],
    }
  }

  addConfiguration() {
    let configuration = {}
    if (!this.resource.gpoPortConfigurations) {
      this.resource.gpoPortConfigurations = []
    }
    this.resource.gpoPortConfigurations.push(configuration)
  }

  removeConfiguration(index) {
    this.resource.gpoPortConfigurations.splice(index, 1)
  }

  getPlaces(value) {
    return this.Place.search({ value }).$promise
  }

  /** Query to get stock zones for md-autocomplete */
  getZones(description = '') {
    return this.Zone.query({
      description,
      zoneType: 'STOCK',
      'place.id': this.resource.place.id,
    }).$promise
  }

  onPlaceChange() {
    this.antennaCards.forEach(antenna => {
      antenna.zone = null
      Object.assign(antenna, this.tmpAntennas[antenna.rfidReaderPort])
      if (
        !antenna.description &&
        !antenna.code &&
        !antenna.zone &&
        !antenna.txPower &&
        !antenna.rxSensitivity
      )
        antenna.$adding = false
    })
    this.updatePlaceZones()
  }

  onTypeChange() {
    // Default values for conditional fields.
    if (this.resource.type == 'ImpinjSpeedwayOctaneDto') {
      if (!this.resource.readerMode)
        this.resource.readerMode = 'AUTO_SET_DENSE_READER'
      if (!this.resource.searchMode) this.resource.searchMode = 'DUAL_TARGET'
      if (!this.resource.session) this.resource.session = 0
    }
    if (this.hasConnectionMode()) {
      if (!this.resource.connectionMode) {
        this.resource.connectionMode = 'ETHERNET'
      }
    } else {
      delete this.resource.connectionMode
    }
    if (this.resource.type === 'CaenQuattroDto') {
      if (!this.resource.memoryBank) {
        this.resource.memoryBank = 'EPC'
      }
      if (!this.resource.gpioPollingTime) {
        this.resource.gpioPollingTime = 100
      }
      if (!this.resource.debounceTime) {
        this.resource.debounceTime = 100
      }
    } else {
      delete this.resource.memoryBank
      delete this.resource.gpioPollingTime
    }

    delete this.resource.gpoPortConfigurations
    this.updateAntennaCards()
  }

  updatePlaceZones() {
    this.placeZones.length = 0
    if (this.resource.place) {
      this.getZones().then(zones => {
        this.placeZones.push(...zones)
        if (this.placeZones.length == 1)
          this.antennaCards.forEach(
            antenna => (antenna.zone = this.placeZones[0])
          )
      })
    }
  }

  updateAntennaCards() {
    const ports = this.resource.$getTypePorts()
    if (!ports) {
      this.antennaCards.length = 0
      return
    }
    // Collect removed antennas and add to delete queue.
    this.antennaCards.slice(ports - 1).forEach(antenna => {
      if (antenna.id) this.antennasToDelete.push(antenna)
    })
    // Rebuild cards.
    this.antennaCards.length = 0
    for (let rfidReaderPort = 1; rfidReaderPort <= ports; rfidReaderPort++) {
      let antenna =
        this.antennas.find(
          antenna => antenna.rfidReaderPort == rfidReaderPort
        ) || this.createAntenna(rfidReaderPort)
      this.antennaCards.push(antenna)
    }
  }

  addAntenna(antenna) {
    antenna.$adding = true
    return this.editAntenna(antenna)
  }

  editAntenna(antenna) {
    antenna.$edited = true
    const { rfidReaderPort, description, code, zone, txPower, rxSensitivity } =
      antenna
    this.tmpAntennas[rfidReaderPort] = {
      description,
      code,
      zone,
      txPower,
      rxSensitivity,
    }
  }

  createAntenna(rfidReaderPort) {
    return this.Antenna.create({ rfidReader: this.resource, rfidReaderPort })
  }

  deleteAntenna(antenna) {
    if (antenna.id) this.antennasToDelete.push(antenna)
    let idx = this.antennaCards.findIndex(
      ({ rfidReaderPort }) => rfidReaderPort == antenna.rfidReaderPort
    )
    this.antennaCards.splice(idx, 1, this.createAntenna(antenna.rfidReaderPort))
  }

  save() {
    let antennasToSave = this.antennaCards.filter(
      antenna => (antenna.id || antenna.$adding) && antenna.$edited
    )
    // Save reader.
    return (
      super
        .save()
        // Save antennas in series.
        .then(() =>
          this.TmrSerialQ(antennasToSave, antenna => {
            // The new reader has now an id, which is required by antenna.
            antenna.rfidReader.id = this.resource.id
            antenna.$save()
          })
        )
        // Delete removed antennas in series.
        .then(() =>
          this.TmrSerialQ(this.antennasToDelete, antenna => antenna.$remove())
        )
    )
  }

  hasConnectionMode() {
    return (
      this.resource.type == 'NordicidSampoNativeDto' ||
      this.resource.type == 'CaenSlateDto' ||
      this.resource.type == 'CaenQuattroDto'
    )
  }

  hasIpAddress() {
    return (
      this.resource.type &&
      this.resource.type !== 'FeigRfidReaderDto' &&
      this.resource.type !== 'KitnloLabidDto' &&
      this.resource.connectionMode !== 'USB'
    )
  }

  hasPort() {
    return (
      this.resource.type === 'ZebraReaderDto' ||
      (this.resource.type === 'CaenQuattroDto' &&
        this.resource.connectionMode === 'USB')
    )
  }
}
